import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Building2, Plus, Package, Trash2, LayoutDashboard, Settings, Euro, LogOut, Users, MessageSquare, Pencil, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import ImageUploader from '../components/ImageUploader';
import Logo from '../components/Logo';

const AdminOptions = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    description_editable: false,
    pourcentage_concierge: 70,
    pourcentage_app: 30,
    prix_fixe: '',
    icon: 'Package',
    image_url: '',
    question_enabled: false,
    question_text: '',
    option_type: 'standard',
  });
  const [editFormData, setEditFormData] = useState({
    nom: '',
    description: '',
    description_editable: false,
    pourcentage_concierge: 70,
    pourcentage_app: 30,
    prix_fixe: '',
    icon: 'Package',
    image_url: '',
    question_enabled: false,
    question_text: '',
    option_type: 'standard',
  });

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await api.get('/admin/options');
      setOptions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des options');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (option) => {
    setEditingOption(option);
    setEditFormData({
      nom: option.nom || '',
      description: option.description || '',
      description_editable: option.description_editable || false,
      pourcentage_concierge: option.pourcentage_concierge || 70,
      pourcentage_app: option.pourcentage_app || 30,
      prix_fixe: option.prix_fixe || '',
      icon: option.icon || 'Package',
      image_url: option.image_url || '',
      question_enabled: option.question_enabled || false,
      question_text: option.question_text || '',
      option_type: option.option_type || 'standard',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    const total = parseFloat(editFormData.pourcentage_concierge) + parseFloat(editFormData.pourcentage_app);
    if (Math.abs(total - 100) > 0.01) {
      toast.error('La somme des pourcentages doit être égale à 100%');
      return;
    }

    try {
      const payload = {
        ...editFormData,
        prix_fixe: editFormData.option_type === 'prestataire' ? null : (editFormData.prix_fixe ? parseFloat(editFormData.prix_fixe) : null),
        image_url: editFormData.image_url || null,
        question_text: editFormData.question_enabled ? editFormData.question_text : null,
      };
      
      await api.put(`/admin/options/${editingOption.id}`, payload);
      toast.success('Option modifiée avec succès !');
      setEditDialogOpen(false);
      setEditingOption(null);
      fetchOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const total = parseFloat(formData.pourcentage_concierge) + parseFloat(formData.pourcentage_app);
    if (Math.abs(total - 100) > 0.01) {
      toast.error('La somme des pourcentages doit être égale à 100%');
      return;
    }

    try {
      const payload = {
        ...formData,
        prix_fixe: formData.option_type === 'prestataire' ? null : (formData.prix_fixe ? parseFloat(formData.prix_fixe) : null),
        image_url: formData.image_url || null,
        question_text: formData.question_enabled ? formData.question_text : null,
      };
      
      await api.post('/admin/options', payload);
      toast.success('Option créée avec succès !');
      setDialogOpen(false);
      setFormData({ 
        nom: '', 
        description: '', 
        description_editable: false,
        pourcentage_concierge: 70, 
        pourcentage_app: 30, 
        prix_fixe: '',
        icon: 'Package',
        image_url: '',
        question_enabled: false,
        question_text: '',
        option_type: 'standard',
      });
      fetchOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDelete = async (optionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette option ?')) return;

    try {
      await api.delete(`/admin/options/${optionId}`);
      toast.success('Option supprimée');
      fetchOptions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-100 p-6 space-y-8">
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
          <Logo variant="full" size="h-12" />
        </div>
        <nav className="space-y-2">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <LayoutDashboard className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Dashboard
          </Button>
          <Button variant="ghost" className="w-full justify-start text-rose-600 bg-rose-50 hover:bg-rose-100">
            <Settings className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Options Globales
          </Button>
          <Button variant="ghost" onClick={() => navigate('/admin/revenue')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <Euro className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Paiements Concierges
          </Button>
          <Button variant="ghost" onClick={() => navigate('/admin/concierges')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <Users className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Liste Concierges
          </Button>
        </nav>
        
        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            onClick={() => { localStorage.clear(); navigate('/auth'); }}
            className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Déconnexion
          </Button>
        </div>
      </aside>

      <div className="ml-64">
        <header className="bg-white border-b border-slate-100 px-8 py-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Options Globales</h1>
            <p className="text-base text-slate-600 mt-1">Gérez les options disponibles pour tous les concierges</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-global-option-btn" className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Ajouter une option
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouvelle Option Globale</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <Label className="text-sm font-medium mb-2 block">Type d'option *</Label>
                  <Select
                    value={formData.option_type}
                    onValueChange={(value) => setFormData({ ...formData, option_type: value })}
                  >
                    <SelectTrigger className="w-full" data-testid="option-type-select">
                      <SelectValue placeholder="Sélectionner le type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-rose-600" />
                          <span>Option standard</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="prestataire">
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-purple-600" />
                          <span>Option avec prestataire externe</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.option_type === 'prestataire' && (
                    <p className="text-xs text-purple-600 mt-2 bg-purple-50 p-2 rounded">
                      Le concierge définira le titre final, le prix et les coordonnées du prestataire. Les pourcentages s'appliquent sur la marge restante (prix client - montant prestataire).
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="nom">Nom de l'option *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    data-testid="option-nom-input"
                    placeholder={formData.option_type === 'prestataire' ? "Ex: Option avec prestataire 1" : "Ex: Champagne à l'arrivée"}
                    className="mt-1"
                  />
                  {formData.option_type === 'prestataire' && (
                    <p className="text-xs text-slate-500 mt-1">Ce nom est interne. Le concierge définira le titre affiché au voyageur.</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="option-description-input"
                    placeholder={formData.option_type === 'prestataire' ? "Description générique pour les activités locales" : "Laissez vide si vous voulez que les concierges remplissent eux-mêmes"}
                    className="mt-1"
                    rows={3}
                  />
                </div>

                <div className="flex items-center space-x-2 p-3 bg-rose-50 rounded-lg">
                  <Switch
                    id="description_editable"
                    checked={formData.description_editable}
                    onCheckedChange={(checked) => setFormData({ ...formData, description_editable: checked })}
                    data-testid="option-description-editable-switch"
                  />
                  <div className="flex-1">
                    <Label htmlFor="description_editable" className="cursor-pointer font-medium">
                      Description personnalisable par concierge
                    </Label>
                    <p className="text-xs text-slate-600 mt-1">
                      Si activé, chaque concierge pourra personnaliser la description pour son logement
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pourcentage_concierge">% Concierge {formData.option_type === 'prestataire' ? '(sur marge)' : ''} *</Label>
                    <Input
                      id="pourcentage_concierge"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.pourcentage_concierge}
                      onChange={(e) => setFormData({ ...formData, pourcentage_concierge: parseFloat(e.target.value) })}
                      required
                      data-testid="option-concierge-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pourcentage_app">% Application {formData.option_type === 'prestataire' ? '(sur marge)' : ''} *</Label>
                    <Input
                      id="pourcentage_app"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.pourcentage_app}
                      onChange={(e) => setFormData({ ...formData, pourcentage_app: parseFloat(e.target.value) })}
                      required
                      data-testid="option-app-input"
                      className="mt-1"
                    />
                  </div>
                </div>

                {formData.option_type === 'standard' && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <Label htmlFor="prix_fixe">Prix fixe (€) - Optionnel</Label>
                    <Input
                      id="prix_fixe"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.prix_fixe}
                      onChange={(e) => setFormData({ ...formData, prix_fixe: e.target.value })}
                      data-testid="option-prix-fixe-input"
                      placeholder="Laisser vide pour prix libre"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-600 mt-2">
                      Si vous définissez un prix fixe, les concierges ne pourront pas le modifier. Laissez vide pour qu'ils choisissent librement.
                    </p>
                  </div>
                )}

                <div>
                  <ImageUploader
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Image de l'option"
                  />
                </div>

                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                  <Switch
                    id="question_enabled"
                    checked={formData.question_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, question_enabled: checked })}
                    data-testid="option-question-enabled-switch"
                  />
                  <div className="flex-1">
                    <Label htmlFor="question_enabled" className="cursor-pointer font-medium flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-blue-600" />
                      Poser une question au voyageur
                    </Label>
                    <p className="text-xs text-slate-600 mt-1">
                      Si activé, le voyageur devra répondre à une question lors de la sélection de cette option
                    </p>
                  </div>
                </div>

                {formData.question_enabled && (
                  <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                    <Label htmlFor="question_text">Question à poser au voyageur *</Label>
                    <Textarea
                      id="question_text"
                      value={formData.question_text}
                      onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                      data-testid="option-question-text-input"
                      placeholder="Ex: Quel type de restaurant préférez-vous et quel est votre budget ?"
                      className="mt-1"
                      rows={2}
                      required={formData.question_enabled}
                    />
                  </div>
                )}

                <Button type="submit" data-testid="option-submit-btn" className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                  Créer l'option
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </header>

        <main className="p-8">
          {options.length === 0 ? (
            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-12 text-center" data-testid="empty-options-state">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Aucune option globale</h3>
              <p className="text-sm text-slate-500 mb-6">Créez des options que les concierges pourront proposer</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Ajouter une option
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="options-grid">
              {options.map((option) => (
                <Card key={option.id} className="bg-white border border-slate-100 shadow-sm rounded-xl overflow-hidden hover-lift" data-testid={`option-card-${option.id}`}>
                  {option.image_url && (
                    <div className="w-full h-40 overflow-hidden bg-slate-100">
                      <img 
                        src={option.image_url} 
                        alt={option.nom}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="p-6 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{option.nom}</h3>
                          {option.option_type === 'prestataire' && (
                            <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                              <UserCheck className="w-3 h-3" />
                              Prestataire
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{option.description || <span className="italic text-slate-400">Pas de description</span>}</p>
                        
                        {option.description_editable && (
                          <span className="inline-block text-xs bg-rose-100 text-rose-600 px-2 py-1 rounded">
                            Description personnalisable
                          </span>
                        )}
                        
                        {option.prix_fixe && option.option_type !== 'prestataire' && (
                          <span className="inline-block text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded ml-2">
                            Prix fixe: {option.prix_fixe} €
                          </span>
                        )}

                        {option.question_enabled && (
                          <div className="mt-2">
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              <MessageSquare className="w-3 h-3" />
                              Question activée
                            </span>
                            {option.question_text && (
                              <p className="text-xs text-blue-600 mt-1 italic">"{option.question_text}"</p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(option)}
                          data-testid={`edit-option-${option.id}`}
                          className="text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                        >
                          <Pencil className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(option.id)}
                          data-testid={`delete-option-${option.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-rose-50 rounded-lg">
                        <p className="text-xs text-rose-600 mb-1">Concierge {option.option_type === 'prestataire' && '(sur marge)'}</p>
                        <p className="text-lg font-bold text-rose-600 tabular-nums">{option.pourcentage_concierge}%</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-600 mb-1">Application {option.option_type === 'prestataire' && '(sur marge)'}</p>
                        <p className="text-lg font-bold text-slate-700 tabular-nums">{option.pourcentage_app}%</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Dialog d'édition */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Modifier l'option</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit_nom">Nom de l'option *</Label>
              <Input
                id="edit_nom"
                value={editFormData.nom}
                onChange={(e) => setEditFormData({ ...editFormData, nom: e.target.value })}
                required
                data-testid="edit-option-nom-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit_description">Description</Label>
              <Textarea
                id="edit_description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                data-testid="edit-option-description-input"
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="edit_description_editable"
                checked={editFormData.description_editable}
                onCheckedChange={(checked) => setEditFormData({ ...editFormData, description_editable: checked })}
                data-testid="edit-option-desc-editable-switch"
              />
              <Label htmlFor="edit_description_editable" className="cursor-pointer">
                Permettre aux concierges de personnaliser la description
              </Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_pourcentage_concierge">% Concierge {editFormData.option_type === 'prestataire' ? '(sur marge)' : ''} *</Label>
                <Input
                  id="edit_pourcentage_concierge"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={editFormData.pourcentage_concierge}
                  onChange={(e) => setEditFormData({ ...editFormData, pourcentage_concierge: parseFloat(e.target.value) })}
                  required
                  data-testid="edit-option-concierge-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit_pourcentage_app">% Application {editFormData.option_type === 'prestataire' ? '(sur marge)' : ''} *</Label>
                <Input
                  id="edit_pourcentage_app"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={editFormData.pourcentage_app}
                  onChange={(e) => setEditFormData({ ...editFormData, pourcentage_app: parseFloat(e.target.value) })}
                  required
                  data-testid="edit-option-app-input"
                  className="mt-1"
                />
              </div>
            </div>

            {editFormData.option_type === 'standard' && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <Label htmlFor="edit_prix_fixe">Prix fixe (€) - Optionnel</Label>
                <Input
                  id="edit_prix_fixe"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editFormData.prix_fixe}
                  onChange={(e) => setEditFormData({ ...editFormData, prix_fixe: e.target.value })}
                  data-testid="edit-option-prix-fixe-input"
                  placeholder="Laisser vide pour prix libre"
                  className="mt-1"
                />
              </div>
            )}
            
            {editFormData.option_type === 'prestataire' && (
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-700">
                  <UserCheck className="w-4 h-4 inline mr-1" />
                  Option avec prestataire externe
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Le prix et les coordonnées du prestataire sont définis par le concierge pour chaque logement.
                </p>
              </div>
            )}

            <div>
              <ImageUploader
                value={editFormData.image_url}
                onChange={(url) => setEditFormData({ ...editFormData, image_url: url })}
                label="Image de l'option"
              />
            </div>

            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
              <Switch
                id="edit_question_enabled"
                checked={editFormData.question_enabled}
                onCheckedChange={(checked) => setEditFormData({ ...editFormData, question_enabled: checked })}
                data-testid="edit-option-question-enabled-switch"
              />
              <div className="flex-1">
                <Label htmlFor="edit_question_enabled" className="cursor-pointer font-medium flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                  Poser une question au voyageur
                </Label>
              </div>
            </div>

            {editFormData.question_enabled && (
              <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                <Label htmlFor="edit_question_text">Question à poser au voyageur *</Label>
                <Textarea
                  id="edit_question_text"
                  value={editFormData.question_text}
                  onChange={(e) => setEditFormData({ ...editFormData, question_text: e.target.value })}
                  data-testid="edit-option-question-text-input"
                  placeholder="Ex: Quel type de restaurant préférez-vous ?"
                  className="mt-1"
                  rows={2}
                  required={editFormData.question_enabled}
                />
              </div>
            )}

            <Button type="submit" data-testid="edit-option-submit-btn" className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
              Enregistrer les modifications
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOptions;
